import { Button, Stack, Tooltip } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Iconify from "../../components/iconify/Iconify";
import { selectCurrentRoles, selectCurrentUserData } from "../auth/authSlice";
import { ROLES_LIST } from "../roles/roles.config";
import {
  selectIsPreviewActive,
  selectOriginPath,
  selectPreviewRole,
  startRolePreview,
  stopRolePreview,
} from "./rolePreviewSlice";

const SWITCH_ROLES = [ROLES_LIST.User, ROLES_LIST.Editor, ROLES_LIST.Admin];
const MEMBER_VIEW_PATH = "/dashboard/veranstaltungen";
const DEFAULT_STAFF_PATH = "/dashboard/app";

export default function MemberViewSwitch() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const realRoles = useSelector(selectCurrentRoles);
  const userData = useSelector(selectCurrentUserData);
  const isPreviewActive = useSelector(selectIsPreviewActive);
  const previewRole = useSelector(selectPreviewRole);
  const originPath = useSelector(selectOriginPath);

  const hasStaffRole =
    Array.isArray(realRoles) &&
    realRoles.some((role) => SWITCH_ROLES.includes(role));
  const hasMemberRef = Boolean(userData?.memberRef);
  const isMemberViewActive =
    isPreviewActive && previewRole === ROLES_LIST.Mitglied;

  if (!hasStaffRole) return null;

  const handleSwitchToMemberView = () => {
    if (!hasMemberRef) return;

    if (isMemberViewActive) {
      if (location.pathname !== MEMBER_VIEW_PATH) {
        navigate(MEMBER_VIEW_PATH);
      }
      return;
    }

    const currentPath = `${location.pathname}${location.search || ""}`;
    dispatch(
      startRolePreview({
        role: ROLES_LIST.Mitglied,
        originPath: currentPath || DEFAULT_STAFF_PATH,
      }),
    );
    navigate(MEMBER_VIEW_PATH);
  };

  const handleSwitchToStaffView = () => {
    if (!isPreviewActive) return;
    dispatch(stopRolePreview());
    navigate(originPath || DEFAULT_STAFF_PATH);
  };

  return (
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="center"
      sx={{
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: 99,
        p: 0.5,
        bgcolor: "background.paper",
        display: { xs: "none", md: "flex" },
      }}
    >
      <Button
        size="small"
        variant={isMemberViewActive ? "outlined" : "contained"}
        onClick={handleSwitchToStaffView}
        startIcon={<Iconify icon="mdi:briefcase-account-outline" width={16} />}
        sx={{ borderRadius: 99, px: 1.25, minWidth: 0, whiteSpace: "nowrap" }}
      >
        Mitarbeiteransicht
      </Button>

      <Tooltip
        arrow
        title={
          hasMemberRef
            ? "Mitgliedsansicht öffnen"
            : "Nicht verfügbar: Kein Mitglied ist mit diesem Konto verknüpft."
        }
      >
        <span>
          <Button
            size="small"
            variant={isMemberViewActive ? "contained" : "outlined"}
            onClick={handleSwitchToMemberView}
            disabled={!hasMemberRef}
            startIcon={<Iconify icon="mdi:account-heart-outline" width={16} />}
            sx={{ borderRadius: 99, px: 1.25, minWidth: 0, whiteSpace: "nowrap" }}
          >
            Mitgliedsansicht
          </Button>
        </span>
      </Tooltip>
    </Stack>
  );
}
